import React, { useCallback, useRef } from "react";

import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  HStack,
} from "@chakra-ui/react";

import { ConfirmState } from "./ConfirmContext";

function Confirm({
  isOpen,
  title,
  message,
  buttons,
  size = "md",
  onClose,
  loadingIndex,
  setLoadingIndex,
}: ConfirmState & {
  onClose: () => void;
  setLoadingIndex: (index: number) => void;
}) {
  const cancelRef = useRef(null);

  const buttonAction = useCallback(
    (index: number, onClick?: ConfirmState["buttons"][number]["onClick"]) =>
      async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setLoadingIndex(index);

        if (onClick && typeof onClick === "function") {
          await onClick(e);
        }

        setLoadingIndex(-1);
        onClose();
      },
    [setLoadingIndex, onClose]
  );

  const onCloseControlled = useCallback(() => {
    if (loadingIndex === -1) {
      onClose();
    }
  }, [onClose, loadingIndex]);

  return (
    <AlertDialog
      isOpen={isOpen}
      leastDestructiveRef={cancelRef}
      onClose={onCloseControlled}
      size={size}
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            {title}
          </AlertDialogHeader>

          <AlertDialogBody>{message}</AlertDialogBody>

          <AlertDialogFooter>
            <HStack
              spacing={3}
            >
              {buttons.map(({ text, onClick, ...buttonProps }, index) => (
                <Button
                  key={index}
                  disabled={loadingIndex !== -1 && loadingIndex !== index}
                  isLoading={loadingIndex === index}
                  {...buttonProps}
                  onClick={buttonAction(index, onClick)}
                  ref={index === 0 ? cancelRef : undefined}
                >
                  {text}
                </Button>
              ))}
            </HStack>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
}

export default React.memo(Confirm);
